import { useQuery } from '@apollo/client';
import { IconButton, Pivot, PivotItem, Stack } from '@fluentui/react';
import { HistoryActionMenuView } from 'common/components/History';
import { PivotCustomRender } from 'common/components/PivotComponent';
import { PivotDisplay } from 'common/components/PivotDisplay';
import { GlobalActions } from 'common/constants';
import { ListInvoiceNotesVariables } from 'common/graphql/__generated__/ListInvoiceNotes';
import React, { useCallback, useState } from 'react';
import { Approval } from '../__generated__/Approval';
import { ApprovalSetupNotesSection } from './ApprovalSetupNotesSection';
import {
  ListApprovalNotes,
  ListApprovalNotesVariables,
} from './ApprovalSetupNotesSection/__generated__/ListApprovalNotes';
import { useStyles } from './index.styles';
import ENTITY_NOTES_LIST from './ApprovalSetupNotesSection/ListApprovalNotes.graphql';

interface ApprovalDetailsProps {
  approvalDetails: Approval | undefined;
}
export const ActionsMenu: React.FC<ApprovalDetailsProps> = ({
  approvalDetails,
}) => {
  const [selectedKey, setSelectedKey] = React.useState<string | undefined>(
    GlobalActions.notes
  );
  const [expandMenu, setExpandMenu] = useState(false);
  const styles = useStyles();
  const { data: entityNotesData, refetch: entityNotesListRefetch } = useQuery<
    ListApprovalNotes,
    ListApprovalNotesVariables
  >(ENTITY_NOTES_LIST, {
    variables: {
      id: approvalDetails?.approval?.id!,
    },
  });

  const getEntityNotes = () => {
    const variables: ListInvoiceNotesVariables = {
      id: approvalDetails?.approval?.id!,
    };
    entityNotesListRefetch(variables);
  };
  const getEntityNotesMemo = useCallback(getEntityNotes, [
    approvalDetails?.approval?.id,
  ]);

  const getSelectedSection = (key: string) => {
    switch (key) {
      case GlobalActions.notes:
        return (
          <ApprovalSetupNotesSection
            data={approvalDetails?.approval?.notesByEntityId.nodes!}
            listRefreshRequest={getEntityNotesMemo}
            id={approvalDetails?.approval?.id!}
          />
        );
      case GlobalActions.history:
        return (
          <HistoryActionMenuView
            moduleName="Approval Setup"
            numberOfItems={4}
            historyData={
              approvalDetails?.approval?.approvalSetupHistoriesByEntityId
                .nodes || []
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <Stack>
      {expandMenu ? (
        <Stack>
          <Stack horizontalAlign="space-between" horizontal>
            <Stack horizontal className={styles.centerAlign}>
              <Pivot
                selectedKey={selectedKey}
                onLinkClick={(item: PivotItem | undefined) => {
                  setSelectedKey(item?.props.itemKey!);
                }}
              >
                <PivotItem
                  itemKey={GlobalActions.notes}
                  itemIcon="Page"
                  headerText="Notes"
                  itemCount={
                    approvalDetails?.approval?.notesByEntityId.nodes.length
                  }
                  onRenderItemLink={PivotCustomRender}
                />
                <PivotItem
                  itemKey={GlobalActions.history}
                  itemIcon="History"
                  headerText="History"
                  itemCount={
                    approvalDetails?.approval?.approvalSetupHistoriesByEntityId
                      .nodes.length
                  }
                  onRenderItemLink={PivotCustomRender}
                />
              </Pivot>
              <IconButton
                onClick={() => setExpandMenu(false)}
                iconProps={{ iconName: 'ChevronUp' }}
                className={styles.iconButtonColor}
                ariaLabel="Collapse"
              />
            </Stack>
          </Stack>

          <Stack>{getSelectedSection(selectedKey!)}</Stack>
        </Stack>
      ) : (
        <Stack horizontalAlign="space-between" horizontal>
          <Stack horizontal verticalAlign="center">
            <Stack horizontal verticalAlign="center">
              <PivotDisplay
                name="Notes"
                iconName="Page"
                count={entityNotesData?.approval?.notesByEntityId.nodes.length}
                onPivotClick={() => {
                  setSelectedKey(GlobalActions.notes);
                  setExpandMenu(true);
                }}
              />
              <PivotDisplay
                name="History"
                iconName="History"
                count={
                  approvalDetails?.approval?.approvalSetupHistoriesByEntityId
                    .nodes.length
                }
                onPivotClick={() => {
                  setSelectedKey(GlobalActions.history);
                  setExpandMenu(true);
                }}
              />
            </Stack>
            <IconButton
              onClick={() => setExpandMenu(true)}
              iconProps={{ iconName: 'ChevronDown' }}
              className={styles.iconButtonColor}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
